import type { ExtractRouteParams } from 'react-router';
import { matchPath, useLocation } from 'react-router';

import type { Project } from 'src/shared/graphql/generated';
import {
  useAiOriginalImageQuery,
  useGetProjectQuery,
} from 'src/shared/graphql/generated';
import { Route } from 'src/shared/route';

import { customizeReferrerSchema } from '../../schemas';

const projectPath = Route.HOMEOWNER_APP_PROJECT.path;
const originalPath = Route.HOMEOWNER_APP_ORIGINAL.path;

type ProjectRouteParams = ExtractRouteParams<typeof projectPath, string>;
type OriginalRouteParams = ExtractRouteParams<typeof originalPath, string>;

export const useReferringProject = () => {
  const location = useLocation();

  const { state } = location;
  const referrer = customizeReferrerSchema.isValidSync(state)
    ? state.referrer
    : null;

  const pathname = referrer ?? location.pathname;

  const { projectId } =
    matchPath<ProjectRouteParams>(pathname, Route.HOMEOWNER_APP_PROJECT.path)
      ?.params ?? {};

  const { projectId: embedProjectId } =
    matchPath<ProjectRouteParams>(pathname, Route.EMBED_PROJECT.path)?.params ??
    {};

  const { aiOriginalImageId } =
    matchPath<OriginalRouteParams>(pathname, Route.HOMEOWNER_APP_ORIGINAL.path)
      ?.params ?? {};

  const { data: { aiOriginalImage } = {}, loading: originalImageLoading } =
    useAiOriginalImageQuery({
      variables: {
        id: aiOriginalImageId,
      },
      skip: !aiOriginalImageId,
    });

  const projectIdCandidate = Number(
    projectId || aiOriginalImage?.projects?.[0]?.id || embedProjectId,
  );

  const { data, loading: projectsLoading } = useGetProjectQuery({
    variables: {
      projectId: Number(projectIdCandidate),
    },
    skip: !projectIdCandidate,
  });

  const { project } = data || {};

  return {
    referreringProject: (project as Project) ?? undefined,
    loading: projectsLoading || originalImageLoading,
  };
};
